<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, type ComputedRef, type Ref } from "vue";
import {
  commitProbeEnrollmentDisplayName,
  createProbeEnrollment,
  readProbeEnrollment,
  revokeProbeEnrollment,
  type ProbeEnrollmentState,
  type ProbeEnrollmentTicket,
} from "../api/probe-enrollments.js";
import {
  deleteProbe,
  listTenantProbes,
  renameProbe,
  requestProbeFirmwareUpdate,
  type ProbeInventoryItem,
  type ProbeHealthStatus,
  type ProbeUploadDiagnosticsSummary,
  saveProbeFirmwarePolicy,
  loadExpectedProviderReconnectPolicy,
  saveExpectedProviderReconnectPolicy,
  type ExpectedProviderReconnectPolicy,
} from "../api/probes.js";
import type { TenantSummary } from "../api/tenants.js";
import AppShell from "../components/AppShell.vue";
import TimePicker from "../components/TimePicker.vue";
import ProbePageTabs from "../components/ProbePageTabs.vue";
import UserActionIcon from "../components/UserActionIcon.vue";
import { confirmDialog, promptDialog } from "../dialogs/app-dialog.js";
import { formatDateTime as formatConfiguredDateTime } from "../formatting/date-time.js";
import { useTenantStore } from "../stores/tenants.js";
import { useUserSettingsStore } from "../stores/user-settings.js";
import {
  loadProbeOperationsSettings,
  type ProbeOperationsSettings,
} from "../api/system-settings.js";
import { loadSystemVersion, type SystemVersion } from "../api/system-information.js";

const tenantStore: ReturnType<typeof useTenantStore> = useTenantStore();
const userSettingsStore: ReturnType<typeof useUserSettingsStore> = useUserSettingsStore();
const tenant: ComputedRef<TenantSummary | null> = computed(
  (): TenantSummary | null => tenantStore.selectedTenant,
);
const probes: Ref<ProbeInventoryItem[]> = ref([]);
const loading: Ref<boolean> = ref(false);
const loadingError: Ref<string | null> = ref(null);
const busyProbeId: Ref<string | null> = ref(null);
const expandedProbeId: Ref<string | null> = ref(null);
type ProbeDetailsTab = "settings" | "diagnostics" | "update-state";
const activeProbeDetailsTab: Ref<ProbeDetailsTab> = ref("settings");
const policyDrafts: Ref<Record<string, {
  channelOverride: "inherit" | "stable" | "beta";
  updateModeOverride: "inherit" | "manual" | "automatic" | "disabled";
}>> = ref({});
type ExpectedProviderReconnectPolicyDraft = {
  enabled: boolean; timezone: string; windowStartLocal: string; windowEndLocal: string; maxDurationMs: number; createdAt: string; updatedAt: string;
};
const reconnectPolicyDrafts: Ref<Record<string, ExpectedProviderReconnectPolicyDraft>> = ref({});
const usesTwelveHourClock: ComputedRef<boolean> = computed((): boolean => {
  const preference = userSettingsStore.settings.dateTimeFormat;
  return preference === "us_12h" || (preference === "browser" && new Intl.DateTimeFormat(undefined, { timeStyle: "short" }).resolvedOptions().hour12 === true);
});
const supportedTimeZones: readonly string[] = (() : readonly string[] => {
  if (typeof Intl.supportedValuesOf === "function") {
    return Intl.supportedValuesOf("timeZone");
  }
  return ["UTC", "Europe/Berlin"];
})();
const enrollmentModalOpen: Ref<boolean> = ref(false);
const enrollmentTicket: Ref<ProbeEnrollmentTicket | null> = ref(null);
const enrollmentError: Ref<string | null> = ref(null);
const enrollmentStatusMessage: Ref<string | null> = ref(null);
const enrollmentPayloadStatusMessage: Ref<string | null> = ref(null);
const enrollmentApiUrl: Ref<string | null> = ref(null);
const enrollmentRequestedDisplayName: Ref<string> = ref("");
const enrollmentDisplayNameError: Ref<string | null> = ref(null);
const creatingEnrollment: Ref<boolean> = ref(false);
const cancellingEnrollment: Ref<boolean> = ref(false);
const now: Ref<number> = ref(Date.now());
const DEFAULT_PROBE_OPERATIONS_SETTINGS: ProbeOperationsSettings = {
  enrollmentCodeTtlSeconds: 600,
  enrollmentClaimRateLimitMax: 20,
  enrollmentClaimRateLimitWindowSeconds: 60,
  uploadDiagnosticsRetentionDays: 1,
  uploadWarningWifiRssiDbm: -80,
  uploadWarningConsecutiveFailures: 1,
  uploadWarningQueueUsageRatio: 0.8,
  updatedAt: "",
};
const probeOperationsSettings: Ref<ProbeOperationsSettings> = ref(DEFAULT_PROBE_OPERATIONS_SETTINGS);
const timerIntervalId: number = window.setInterval((): void => {
  now.value = Date.now();
  if (enrollmentModalOpen.value && enrollmentTicket.value !== null && remainingSeconds.value <= 0) {
    closeEnrollmentModal();
  }
}, 1000);
let pollIntervalId: number | null = null;
const PROBE_DETAILS_POLL_INTERVAL_MS: number = 10_000;
let probeDetailsPollIntervalId: number | null = null;
let committedEnrollmentDisplayName: string | null = null;
let pollingEnrollment: boolean = false;
let pollingProbeDetails: boolean = false;

const healthyCount: ComputedRef<number> = computed(
  (): number => probes.value.filter((probe: ProbeInventoryItem): boolean => probe.healthStatus === "healthy").length,
);
const overdueCount: ComputedRef<number> = computed(
  (): number => probes.value.filter((probe: ProbeInventoryItem): boolean => probe.overdueMs > 0).length,
);
const neverReportedCount: ComputedRef<number> = computed(
  (): number => probes.value.filter((probe: ProbeInventoryItem): boolean =>
    probe.healthStatus === "never_reported" && probe.enrollmentStatus !== "bound",
  ).length,
);
const activationIssuedCount: ComputedRef<number> = computed(
  (): number => probes.value.filter((probe: ProbeInventoryItem): boolean => probe.enrollmentStatus === "bound").length,
);
const storageDataLossCount: ComputedRef<number> = computed(
  (): number => probes.value.filter((probe: ProbeInventoryItem): boolean => probe.storageDataLoss !== null).length,
);
const uploadDiagnosticsWarningCount: ComputedRef<number> = computed(
  (): number => probes.value.filter(hasUploadDiagnosticsWarning).length,
);
const registeredProbeCapacity: ComputedRef<number> = computed(
  (): number => tenant.value?.tier.maxProbesPerTenant ?? 0,
);
const canManageProbeLifecycle: ComputedRef<boolean> = computed(
  (): boolean => tenant.value?.access.capabilities.includes("probe.lifecycle.manage") === true,
);
const canExecuteProbeOperations: ComputedRef<boolean> = computed(
  (): boolean => tenant.value?.access.capabilities.includes("probe.operations.execute") === true,
);
const remainingSeconds: ComputedRef<number> = computed((): number => {
  if (enrollmentTicket.value === null) {
    return 0;
  }
  return Math.max(0, Math.ceil((new Date(enrollmentTicket.value.expiresAt).getTime() - now.value) / 1000));
});
const timerText: ComputedRef<string> = computed((): string => {
  const minutes: number = Math.floor(remainingSeconds.value / 60);
  const seconds: number = remainingSeconds.value % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
});
const enrollmentMaterialJson: ComputedRef<string> = computed((): string => {
  const ticket: ProbeEnrollmentTicket | null = enrollmentTicket.value;
  if (ticket === null) {
    return "";
  }
  const material: {
    readonly version: 1;
    readonly serverUrl: string;
    readonly enrollmentCode: string;
    readonly expiresAt: string;
  } = {
    version: 1,
    serverUrl: enrollmentApiUrl.value ?? "",
    enrollmentCode: ticket.displayCode,
    expiresAt: ticket.expiresAt,
  };
  return `${JSON.stringify(material, null, 2)}\n`;
});
const enrollmentSetupCode: ComputedRef<string> = computed((): string => {
  const materialJson: string = enrollmentMaterialJson.value;
  if (materialJson === "") {
    return "";
  }
  return `LS1-${encodeBase64Utf8(materialJson)}`;
});
const enrollmentSetupCodePreview: ComputedRef<string> = computed((): string => {
  const setupCode: string = enrollmentSetupCode.value;
  if (setupCode.length <= 36) {
    return setupCode;
  }
  return `${setupCode.slice(0, 24)}...${setupCode.slice(-8)}`;
});

function formatDateTime(value: string | null): string {
  return formatConfiguredDateTime(value, userSettingsStore.settings.dateTimeFormat);
}

function normalizePublicApiUrl(value: string): string | null {
  const trimmed: string = value.trim();
  if (trimmed === "") {
    return null;
  }
  try {
    const url: URL = new URL(trimmed);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }
    url.hash = "";
    return url.toString().replace(/\/$/, "");
  } catch (_error: unknown) {
    return null;
  }
}

async function loadEnrollmentApiUrl(): Promise<void> {
  try {
    const version: SystemVersion = await loadSystemVersion();
    enrollmentApiUrl.value = normalizePublicApiUrl(version.publicApiUrl);
    if (enrollmentApiUrl.value === null) {
      throw new Error("Public API URL is invalid.");
    }
  } catch (_error: unknown) {
    enrollmentApiUrl.value = null;
    throw new Error("Public API URL could not be loaded for probe enrollment.");
  }
}

function encodeBase64Utf8(value: string): string {
  const bytes: Uint8Array = new TextEncoder().encode(value);
  let binary: string = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return window.btoa(binary);
}

function downloadTextFile(filename: string, content: string, mimeType: string): void {
  const blob: Blob = new Blob([content], { type: mimeType });
  const url: string = window.URL.createObjectURL(blob);
  const link: HTMLAnchorElement = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function enrollmentPayloadFilename(extension: "json" | "txt"): string {
  const ticket: ProbeEnrollmentTicket | null = enrollmentTicket.value;
  const suffix: string = ticket === null ? "pending" : ticket.id;
  return `lacunascope-enrollment-${suffix}.${extension}`;
}

function downloadEnrollmentFile(): void {
  if (enrollmentMaterialJson.value === "") {
    return;
  }
  downloadTextFile(
    enrollmentPayloadFilename("json"),
    enrollmentMaterialJson.value,
    "application/json;charset=utf-8",
  );
  enrollmentPayloadStatusMessage.value = "Enrollment file downloaded.";
}

function downloadEnrollmentSetupCode(): void {
  if (enrollmentSetupCode.value === "") {
    return;
  }
  downloadTextFile(
    enrollmentPayloadFilename("txt"),
    `${enrollmentSetupCode.value}\n`,
    "text/plain;charset=utf-8",
  );
  enrollmentPayloadStatusMessage.value = "Setup code file downloaded.";
}

async function copyEnrollmentSetupCode(): Promise<void> {
  if (enrollmentSetupCode.value === "") {
    return;
  }
  try {
    await navigator.clipboard.writeText(enrollmentSetupCode.value);
    enrollmentPayloadStatusMessage.value = "Setup code copied.";
  } catch (_error: unknown) {
    enrollmentPayloadStatusMessage.value = "Setup code could not be copied; download the text file instead.";
  }
}

function formatDuration(milliseconds: number): string {
  if (milliseconds <= 0) {
    return "not overdue";
  }
  const totalSeconds: number = Math.floor(milliseconds / 1000);
  const days: number = Math.floor(totalSeconds / 86_400);
  const hours: number = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes: number = Math.floor((totalSeconds % 3_600) / 60);
  const seconds: number = totalSeconds % 60;
  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

function formatInterval(milliseconds: number | null): string {
  if (milliseconds === null) {
    return "—";
  }
  if (milliseconds <= 0) {
    return "0s";
  }
  return formatDuration(milliseconds);
}

function formatNumber(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : value.toLocaleString();
}

function formatBytes(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return "—";
  }
  if (value < 1024) {
    return `${value.toLocaleString()} B`;
  }
  const kib: number = value / 1024;
  if (kib < 1024) {
    return `${kib.toFixed(1)} KiB`;
  }
  return `${(kib / 1024).toFixed(1)} MiB`;
}

const HEAP_HEALTH_SCALE_BYTES: number = 128 * 1024;
const HEAP_HEALTH_HEALTHY_BYTES: number = 96 * 1024;
const HEAP_HEALTH_CAUTION_BYTES: number = 64 * 1024;
const HEAP_HEALTH_CRITICAL_BYTES: number = 24 * 1024;

function heapHealthPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return "0%";
  }
  return `${Math.min(100, Math.max(0, (value / HEAP_HEALTH_SCALE_BYTES) * 100))}%`;
}

function heapHealthClass(value: number | null | undefined): string {
  if (value === null || value === undefined || value < HEAP_HEALTH_CRITICAL_BYTES) {
    return "heap-meter__fill--critical";
  }
  if (value < HEAP_HEALTH_CAUTION_BYTES) {
    return "heap-meter__fill--warning";
  }
  if (value < HEAP_HEALTH_HEALTHY_BYTES) {
    return "heap-meter__fill--caution";
  }
  return "heap-meter__fill--healthy";
}

function formatTemperature(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${value.toFixed(1)} °C`;
}

function formatRssi(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${value} dBm`;
}

function rssiQualityPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return "0%";
  }
  return `${Math.min(100, Math.max(0, ((value + 100) / 60) * 100))}%`;
}

function rssiQualityClass(value: number | null | undefined): string {
  if (value === null || value === undefined || value < -80) {
    return "quality-meter__fill--critical";
  }
  if (value < -75) {
    return "quality-meter__fill--warning";
  }
  if (value < -67) {
    return "quality-meter__fill--caution";
  }
  return "quality-meter__fill--healthy";
}

function formatConnectionState(value: boolean | null | undefined): string {
  if (value === null || value === undefined) {
    return "unknown";
  }
  return value ? "connected" : "disconnected";
}

function formatResetReason(code: number | null | undefined, label: string | null | undefined): string {
  if (code === null || code === undefined) {
    return "—";
  }
  return label === null || label === undefined ? `Unknown reset reason (code ${code})` : `${label} (code ${code})`;
}

function formatNullableDuration(milliseconds: number | null | undefined): string {
  return milliseconds === null || milliseconds === undefined ? "—" : formatDuration(milliseconds);
}

function formatTimeSince(value: string): string {
  const timestamp: number = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) {
    return "—";
  }
  return formatDuration(Math.max(0, now.value - timestamp));
}

function formatQueueUsage(diagnostics: ProbeUploadDiagnosticsSummary | null): string {
  const usedBytes: number | null | undefined = diagnostics?.storage?.usedBytes;
  const sizeBytes: number | null | undefined = diagnostics?.storage?.sizeBytes;
  if (usedBytes === null || usedBytes === undefined || sizeBytes === null || sizeBytes === undefined || sizeBytes <= 0) {
    return "—";
  }
  return `${formatBytes(usedBytes)} / ${formatBytes(sizeBytes)} (${Math.round((usedBytes / sizeBytes) * 100)}%)`;
}

function hasUploadDiagnosticsWarning(probe: ProbeInventoryItem): boolean {
  const diagnostics: ProbeUploadDiagnosticsSummary | null = probe.uploadDiagnostics;
  if (diagnostics === null) {
    return false;
  }
  const queueRatio: number | null = diagnostics.storage?.usedBytes !== null &&
    diagnostics.storage?.usedBytes !== undefined &&
    diagnostics.storage?.sizeBytes !== null &&
    diagnostics.storage?.sizeBytes !== undefined &&
    diagnostics.storage.sizeBytes > 0
    ? diagnostics.storage.usedBytes / diagnostics.storage.sizeBytes
    : null;
  return (diagnostics.wifi?.rssi ?? 0) < probeOperationsSettings.value.uploadWarningWifiRssiDbm ||
    (diagnostics.upload?.consecutiveFailures ?? 0) >=
      probeOperationsSettings.value.uploadWarningConsecutiveFailures ||
    (queueRatio ?? 0) > probeOperationsSettings.value.uploadWarningQueueUsageRatio;
}

function overdueLabel(milliseconds: number): string {
  return milliseconds <= 0 ? "On time" : formatDuration(milliseconds);
}

function overdueClass(milliseconds: number): string {
  return milliseconds <= 0 ? "status-badge status-badge--on-time" : "status-badge status-badge--overdue";
}

function isOverdueHealth(probe: ProbeInventoryItem): boolean {
  return probe.healthStatus === "overdue";
}

function progressWidth(value: number, maximum: number): string {
  if (maximum <= 0) {
    return "0%";
  }
  return `${Math.min(100, Math.max(0, (value / maximum) * 100))}%`;
}

function progressTone(value: number, maximum: number, inverted: boolean = false): string {
  if (maximum <= 0) {
    return inverted ? "metric-card__progress--bad" : "metric-card__progress--good";
  }
  const ratio: number = Math.min(1, Math.max(0, value / maximum));
  const riskRatio: number = inverted ? 1 - ratio : ratio;
  if (riskRatio < 0.5) {
    return "metric-card__progress--good";
  }
  if (riskRatio < 0.75) {
    return "metric-card__progress--warning";
  }
  if (riskRatio < 0.9) {
    return "metric-card__progress--caution";
  }
  return "metric-card__progress--bad";
}

function probeHealthLabel(probe: ProbeInventoryItem): string {
  if (probe.enrollmentStatus === "bound") {
    return "Activation issued";
  }
  if (isOverdueHealth(probe)) {
    return "Overdue";
  }
  if (probe.storageDataLoss !== null) {
    return "Storage loss";
  }
  if (hasUploadDiagnosticsWarning(probe)) {
    return "Probe warning";
  }
  const status: ProbeHealthStatus = probe.healthStatus;
  if (status === "never_reported") {
    return "Never reported";
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function probeHealthClass(probe: ProbeInventoryItem): string {
  if (probe.enrollmentStatus === "bound") {
    return "status-badge status-badge--pending";
  }
  if (isOverdueHealth(probe)) {
    return "status-badge status-badge--overdue";
  }
  if (probe.storageDataLoss !== null) {
    return "status-badge status-badge--warning";
  }
  if (hasUploadDiagnosticsWarning(probe)) {
    return "status-badge status-badge--warning";
  }
  return `status-badge status-badge--${probe.healthStatus.replace("_", "-")}`;
}

function formatStorageLoss(probe: ProbeInventoryItem): string {
  if (probe.storageDataLoss === null) {
    return "";
  }
  return `${probe.storageDataLoss.lostMeasurements} measurements discarded locally`;
}

function firmwareBadgeLabel(probe: ProbeInventoryItem): string {
  if (probe.firmware.updateStatus === "mandatory_update") {
    return "Mandatory update";
  }
  if (probe.firmware.updateStatus === "update_disabled") {
    return "Update disabled";
  }
  if (probe.firmware.updateStatus === "update_available") {
    return "Update available";
  }
  return "Current";
}

function firmwareBadgeClass(probe: ProbeInventoryItem): string {
  if (probe.firmware.updateStatus === "mandatory_update") {
    return "status-badge status-badge--mandatory";
  }
  if (probe.firmware.updateStatus === "update_disabled") {
    return "status-badge status-badge--disabled";
  }
  if (probe.firmware.updateStatus === "update_available") {
    return "status-badge status-badge--warning";
  }
  return "status-badge status-badge--active";
}

function updateActionLabel(probe: ProbeInventoryItem): string {
  if (probe.firmware.updateStatus === "mandatory_update") {
    return "Mandatory on report";
  }
  if (probe.firmware.manualUpdatePending) {
    return "Update pending";
  }
  if (probe.firmware.effectiveUpdateMode === "automatic" && probe.firmware.updateAvailable) {
    return "Auto on report";
  }
  if (probe.firmware.effectiveUpdateMode === "disabled") {
    return "Update disabled";
  }
  return "Apply update";
}

function canApplyFirmwareUpdate(probe: ProbeInventoryItem): boolean {
  return canExecuteProbeOperations.value &&
    probe.firmware.otaCapable &&
    probe.firmware.updateAvailable &&
    probe.firmware.updateStatus !== "mandatory_update" &&
    probe.firmware.effectiveUpdateMode === "manual" &&
    !probe.firmware.manualUpdatePending &&
    busyProbeId.value !== probe.id;
}

async function openProbeDetails(probe: ProbeInventoryItem): Promise<void> {
  expandedProbeId.value = probe.id;
  activeProbeDetailsTab.value = "settings";
  startProbeDetailsPolling();
  policyDrafts.value = {
    ...policyDrafts.value,
    [probe.id]: {
      channelOverride: probe.firmware.channelOverride,
      updateModeOverride: probe.firmware.updateModeOverride,
    },
  };
  if (expandedProbeId.value === probe.id && tenant.value !== null && reconnectPolicyDrafts.value[probe.id] === undefined) {
    try {
      reconnectPolicyDrafts.value = { ...reconnectPolicyDrafts.value, [probe.id]: await loadExpectedProviderReconnectPolicy(tenant.value.id, probe.id) };
    } catch (error: unknown) {
      loadingError.value = error instanceof Error ? error.message : "Expected provider reconnect policy could not be loaded.";
    }
  }
}

function closeProbeDetails(): void {
  stopProbeDetailsPolling();
  expandedProbeId.value = null;
}

async function saveReconnectPolicy(probe: ProbeInventoryItem): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  const draft: ExpectedProviderReconnectPolicyDraft | undefined = reconnectPolicyDrafts.value[probe.id];
  if (currentTenant === null || draft === undefined) return;
  busyProbeId.value = probe.id;
  try {
    const saved: ExpectedProviderReconnectPolicy = await saveExpectedProviderReconnectPolicy(currentTenant.id, probe.id, draft);
    reconnectPolicyDrafts.value = { ...reconnectPolicyDrafts.value, [probe.id]: saved };
  } catch (error: unknown) {
    loadingError.value = error instanceof Error ? error.message : "Expected provider reconnect policy could not be saved.";
  } finally { busyProbeId.value = null; }
}


function updatePolicyDraft(
  probe: ProbeInventoryItem,
  key: "channelOverride" | "updateModeOverride",
  value: string,
): void {
  if (
    (key === "channelOverride" && value !== "inherit" && value !== "stable" && value !== "beta") ||
    (key === "updateModeOverride" && value !== "inherit" && value !== "manual" && value !== "automatic" && value !== "disabled")
  ) {
    return;
  }
  const existing = policyDrafts.value[probe.id] ?? {
    channelOverride: probe.firmware.channelOverride,
    updateModeOverride: probe.firmware.updateModeOverride,
  };
  policyDrafts.value = {
    ...policyDrafts.value,
    [probe.id]: key === "channelOverride"
      ? {
        ...existing,
        channelOverride: value as "inherit" | "stable" | "beta",
      }
      : {
        ...existing,
        updateModeOverride: value as "inherit" | "manual" | "automatic" | "disabled",
      },
  };
}

async function saveSelectedProbeFirmwarePolicy(probe: ProbeInventoryItem): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  const draft = policyDrafts.value[probe.id];
  if (currentTenant === null || draft === undefined || busyProbeId.value !== null) {
    return;
  }
  busyProbeId.value = probe.id;
  loadingError.value = null;
  try {
    await saveProbeFirmwarePolicy(currentTenant.id, probe.id, draft.channelOverride, draft.updateModeOverride);
    await loadProbes();
  } catch (error: unknown) {
    loadingError.value = error instanceof Error ? error.message : "Firmware policy could not be saved.";
  } finally {
    busyProbeId.value = null;
  }
}

async function applySelectedProbeFirmwareUpdate(probe: ProbeInventoryItem): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || !canApplyFirmwareUpdate(probe)) {
    return;
  }
  const confirmed: boolean = await confirmDialog({
    title: "Apply firmware update?",
    message: `${probe.displayName} will be instructed to install firmware ${probe.firmware.targetVersion ?? "the latest eligible release"} on its next report.`,
    confirmLabel: "Apply update",
  });
  if (!confirmed) {
    return;
  }
  busyProbeId.value = probe.id;
  loadingError.value = null;
  try {
    await requestProbeFirmwareUpdate(currentTenant.id, probe.id);
    await loadProbes();
  } catch (error: unknown) {
    loadingError.value = error instanceof Error ? error.message : "Firmware update could not be requested.";
  } finally {
    busyProbeId.value = null;
  }
}

async function loadProbes(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null) {
    probes.value = [];
    return;
  }
  loading.value = true;
  loadingError.value = null;
  try {
    probes.value = [...await listTenantProbes(currentTenant.id)];
  } catch (error: unknown) {
    loadingError.value = error instanceof Error ? error.message : "Probe inventory could not be loaded.";
  } finally {
    loading.value = false;
  }
}

async function refreshOpenProbeDetails(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  const probeId: string | null = expandedProbeId.value;
  if (currentTenant === null || probeId === null || pollingProbeDetails) {
    return;
  }

  pollingProbeDetails = true;
  try {
    const refreshedProbes: ProbeInventoryItem[] = [...await listTenantProbes(currentTenant.id)];
    if (tenant.value?.id === currentTenant.id && expandedProbeId.value === probeId) {
      probes.value = refreshedProbes;
      if (!refreshedProbes.some((probe: ProbeInventoryItem): boolean => probe.id === probeId)) {
        closeProbeDetails();
      }
    }
  } catch (_error: unknown) {
    // A transient detail refresh must not replace already visible data or
    // interrupt the operator while they edit probe settings.
  } finally {
    pollingProbeDetails = false;
  }
}

function stopProbeDetailsPolling(): void {
  if (probeDetailsPollIntervalId !== null) {
    window.clearInterval(probeDetailsPollIntervalId);
    probeDetailsPollIntervalId = null;
  }
}

function startProbeDetailsPolling(): void {
  stopProbeDetailsPolling();
  void refreshOpenProbeDetails();
  probeDetailsPollIntervalId = window.setInterval((): void => {
    void refreshOpenProbeDetails();
  }, PROBE_DETAILS_POLL_INTERVAL_MS);
}

async function loadProbeOperationsPolicy(): Promise<void> {
  try {
    probeOperationsSettings.value = await loadProbeOperationsSettings();
  } catch (error: unknown) {
    probeOperationsSettings.value = DEFAULT_PROBE_OPERATIONS_SETTINGS;
  }
}

function stopEnrollmentPolling(): void {
  if (pollIntervalId !== null) {
    window.clearInterval(pollIntervalId);
    pollIntervalId = null;
  }
}

function closeEnrollmentModal(): void {
  stopEnrollmentPolling();
  enrollmentModalOpen.value = false;
  enrollmentTicket.value = null;
  enrollmentError.value = null;
  enrollmentStatusMessage.value = null;
  enrollmentPayloadStatusMessage.value = null;
  enrollmentApiUrl.value = null;
  enrollmentRequestedDisplayName.value = "";
  enrollmentDisplayNameError.value = null;
  creatingEnrollment.value = false;
  committedEnrollmentDisplayName = null;
  cancellingEnrollment.value = false;
}

async function pollEnrollmentState(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  const ticket: ProbeEnrollmentTicket | null = enrollmentTicket.value;
  if (currentTenant === null || ticket === null || pollingEnrollment) {
    return;
  }

  pollingEnrollment = true;
  try {
    const state: ProbeEnrollmentState = await readProbeEnrollment(currentTenant.id, ticket.id);
    if (state.status === "activated") {
      await commitEnrollmentDisplayName(state);
      enrollmentStatusMessage.value = "Activation confirmed.";
      closeEnrollmentModal();
      await loadProbes();
    } else if (state.status === "bound") {
      await commitEnrollmentDisplayName(state);
      enrollmentStatusMessage.value = "Activation issued — waiting for probe confirmation.";
      await loadProbes();
    } else if (state.status === "revoked" || state.status === "expired") {
      closeEnrollmentModal();
      await loadProbes();
    }
  } catch (_error: unknown) {
    // Keep the code visible until the next poll or countdown expiry. Temporary
    // polling failures should not invalidate an otherwise usable deploy code.
  } finally {
    pollingEnrollment = false;
  }
}

function startEnrollmentPolling(): void {
  stopEnrollmentPolling();
  pollIntervalId = window.setInterval((): void => {
    void pollEnrollmentState();
  }, 2000);
}

async function openEnrollmentModal(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || creatingEnrollment.value) {
    return;
  }

  enrollmentModalOpen.value = true;
  enrollmentTicket.value = null;
  enrollmentError.value = null;
  enrollmentStatusMessage.value = null;
  enrollmentPayloadStatusMessage.value = null;
  enrollmentApiUrl.value = null;
  enrollmentRequestedDisplayName.value = "";
  enrollmentDisplayNameError.value = null;
  committedEnrollmentDisplayName = null;
  creatingEnrollment.value = true;
  now.value = Date.now();
  try {
    await loadEnrollmentApiUrl();
    enrollmentTicket.value = await createProbeEnrollment(currentTenant.id, {});
    now.value = Date.now();
    startEnrollmentPolling();
  } catch (error: unknown) {
    enrollmentError.value = error instanceof Error ? error.message : "Probe enrollment could not be created.";
  } finally {
    creatingEnrollment.value = false;
  }
}

async function commitEnrollmentDisplayName(state: ProbeEnrollmentState): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  const ticket: ProbeEnrollmentTicket | null = enrollmentTicket.value;
  const displayName: string = enrollmentRequestedDisplayName.value.trim();
  if (
    currentTenant === null ||
    ticket === null ||
    state.probeId === null ||
    displayName === "" ||
    committedEnrollmentDisplayName === displayName
  ) {
    return;
  }

  try {
    await commitProbeEnrollmentDisplayName(
      currentTenant.id,
      ticket.id,
      displayName,
    );
    committedEnrollmentDisplayName = displayName;
  } catch (error: unknown) {
    enrollmentDisplayNameError.value = error instanceof Error ? error.message : "Probe name could not be saved.";
  }
}

async function cancelEnrollment(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  const ticket: ProbeEnrollmentTicket | null = enrollmentTicket.value;
  if (currentTenant === null || cancellingEnrollment.value) {
    closeEnrollmentModal();
    return;
  }

  cancellingEnrollment.value = true;
  try {
    if (ticket !== null) {
      await revokeProbeEnrollment(currentTenant.id, ticket.id);
    }
  } catch (error: unknown) {
    enrollmentError.value = error instanceof Error ? error.message : "Deployment code could not be cancelled.";
    cancellingEnrollment.value = false;
    return;
  }
  closeEnrollmentModal();
}

async function renameSelectedProbe(probe: ProbeInventoryItem): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || busyProbeId.value !== null) {
    return;
  }

  const displayName: string | null = await promptDialog({
    title: "Rename probe",
    message: `Choose a new display name for ${probe.displayName}.`,
    inputLabel: "Probe display name",
    initialValue: probe.displayName,
    confirmLabel: "Rename",
    cancelTone: "danger",
  });
  const normalizedDisplayName: string = displayName?.trim() ?? "";
  if (normalizedDisplayName === "" || normalizedDisplayName === probe.displayName) {
    return;
  }

  busyProbeId.value = probe.id;
  loadingError.value = null;
  try {
    const renamedProbe: ProbeInventoryItem = await renameProbe(
      currentTenant.id,
      probe.id,
      normalizedDisplayName,
    );
    probes.value = probes.value.map((item: ProbeInventoryItem): ProbeInventoryItem =>
      item.id === renamedProbe.id ? renamedProbe : item,
    );
  } catch (error: unknown) {
    loadingError.value = error instanceof Error ? error.message : "Probe could not be renamed.";
  } finally {
    busyProbeId.value = null;
  }
}

async function deleteSelectedProbe(probe: ProbeInventoryItem): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || busyProbeId.value !== null) {
    return;
  }

  const confirmed: boolean = await confirmDialog({
    title: "Delete probe?",
    message: `${probe.displayName} will be removed from this tenant's active probe list. On its next server contact it should be instructed to return to deployment mode.`,
    confirmLabel: "Delete probe",
    tone: "danger",
  });
  if (!confirmed) {
    return;
  }

  busyProbeId.value = probe.id;
  loadingError.value = null;
  try {
    await deleteProbe(currentTenant.id, probe.id);
    probes.value = probes.value.filter((item: ProbeInventoryItem): boolean => item.id !== probe.id);
  } catch (error: unknown) {
    loadingError.value = error instanceof Error ? error.message : "Probe could not be deleted.";
  } finally {
    busyProbeId.value = null;
  }
}

onMounted((): void => {
  void loadProbeOperationsPolicy();
  void loadProbes();
});
watch((): string | null => tenant.value?.id ?? null, loadProbes);
onBeforeUnmount((): void => {
  window.clearInterval(timerIntervalId);
  stopEnrollmentPolling();
  stopProbeDetailsPolling();
});
</script>

<template>
  <AppShell v-if="tenant">
    <header class="page-heading">
      <div>
        <p class="eyebrow">{{ tenant.slug }}</p>
        <h1>Probes</h1>
        <p>Probe inventory and tenant-wide configuration delivered to registered probes.</p>
      </div>
      <button
        v-if="tenant.access.capabilities.includes('probe.enrollment.manage')"
        class="button button--primary page-heading__action"
        type="button"
        :disabled="creatingEnrollment"
        @click="openEnrollmentModal"
      >
        {{ creatingEnrollment ? "Creating…" : "Add Probe" }}
      </button>
    </header>

    <ProbePageTabs />

    <section class="metric-grid metric-grid--four" aria-label="Probe summary">
      <article class="metric-card">
        <span class="metric-card__label">Registered</span>
        <strong>{{ probes.length }} / {{ registeredProbeCapacity }}</strong>
        <div class="metric-card__progress" :class="progressTone(probes.length, registeredProbeCapacity)" aria-hidden="true">
          <span :style="{ width: progressWidth(probes.length, registeredProbeCapacity) }"></span>
        </div>
        <span>Registered probes available in this tier</span>
      </article>
      <article class="metric-card">
        <span class="metric-card__label">Healthy</span>
        <strong>{{ healthyCount }} / {{ probes.length }}</strong>
        <div class="metric-card__progress" :class="progressTone(healthyCount, probes.length, true)" aria-hidden="true">
          <span :style="{ width: progressWidth(healthyCount, probes.length) }"></span>
        </div>
        <span>Reported within expected interval</span>
      </article>
      <article class="metric-card">
        <span class="metric-card__label">Overdue</span>
        <strong>{{ overdueCount }} / {{ probes.length }}</strong>
        <div class="metric-card__progress" :class="progressTone(overdueCount, probes.length)" aria-hidden="true">
          <span :style="{ width: progressWidth(overdueCount, probes.length) }"></span>
        </div>
        <span>Past next expected upload</span>
      </article>
      <article class="metric-card">
        <span class="metric-card__label">Never reported</span>
        <strong>{{ neverReportedCount }} / {{ probes.length }}</strong>
        <div class="metric-card__progress" :class="progressTone(neverReportedCount, probes.length)" aria-hidden="true">
          <span :style="{ width: progressWidth(neverReportedCount, probes.length) }"></span>
        </div>
        <span>Registered but no upload received</span>
      </article>
    </section>

    <p v-if="activationIssuedCount > 0" class="notice">
      {{ activationIssuedCount }} probe{{ activationIssuedCount === 1 ? "" : "s" }} with activation issued are waiting for probe confirmation.
    </p>
    <p v-if="storageDataLossCount > 0" class="notice notice--warning">
      {{ storageDataLossCount }} probe{{ storageDataLossCount === 1 ? "" : "s" }} reported local buffer overflow. Older measurements were discarded before upload.
    </p>
    <p v-if="uploadDiagnosticsWarningCount > 0" class="notice notice--warning">
      {{ uploadDiagnosticsWarningCount }} probe{{ uploadDiagnosticsWarningCount === 1 ? "" : "s" }} reported weak Wi-Fi, upload recovery, or high queue usage.
    </p>

    <section class="content-panel data-table-panel">
      <div class="content-panel__heading">
        <div>
          <p class="eyebrow">Inventory</p>
          <h2>Registered probes</h2>
        </div>
        <button
          class="icon-button"
          type="button"
          :disabled="loading"
          title="Refresh probes"
          aria-label="Refresh probes"
          @click="loadProbes"
        >
          <UserActionIcon name="refresh" />
        </button>
      </div>

      <p v-if="loadingError" class="form-error">{{ loadingError }}</p>
      <p v-else-if="loading" class="placeholder-panel">Loading probes…</p>
      <p v-else-if="probes.length === 0" class="placeholder-panel">
        No probes are registered for this tenant yet.
      </p>

      <div v-else class="data-table data-table--probes" role="table" aria-label="Registered probes">
        <div class="data-table__header" role="row">
          <span role="columnheader">Probe</span>
          <span role="columnheader">Health</span>
          <span role="columnheader">Last received</span>
          <span role="columnheader">Next expected</span>
          <span role="columnheader">Overdue</span>
          <span role="columnheader">Firmware</span>
          <span role="columnheader">Actions</span>
        </div>
        <template
          v-for="probe in probes"
          :key="probe.id"
        >
        <div
          class="data-table__row"
          :class="{ 'data-table__row--inactive': !probe.enabled }"
          role="row"
          @click="void openProbeDetails(probe)"
        >
          <span role="cell">
            <strong>{{ probe.displayName }}</strong>
            <small>{{ probe.probeModel ?? "unknown probe type" }} · {{ probe.agentVersion ?? "unknown probe version" }}</small>
          </span>
          <span role="cell">
            <span :class="probeHealthClass(probe)">{{ probeHealthLabel(probe) }}</span>
            <small v-if="probe.enrollmentStatus === 'bound'">waiting for activation acknowledgement</small>
            <small v-else-if="!isOverdueHealth(probe) && probe.storageDataLoss !== null">
              {{ formatStorageLoss(probe) }} · {{ probe.storageDataLoss.reason }} · reported {{ formatDateTime(probe.storageDataLoss.lastReportedAt) }}
            </small>
            <small v-else-if="!isOverdueHealth(probe) && hasUploadDiagnosticsWarning(probe)">
              RSSI {{ formatRssi(probe.uploadDiagnostics?.wifi?.rssi) }} · upload failures {{ formatNumber(probe.uploadDiagnostics?.upload?.consecutiveFailures) }}
            </small>
          </span>
          <span role="cell">
            {{ formatDateTime(probe.lastReceivedAt) }}
            <small v-if="probe.lastMeasuredAt">measured {{ formatDateTime(probe.lastMeasuredAt) }}</small>
          </span>
          <span role="cell">
            {{ formatDateTime(probe.nextExpectedAt) }}
            <small>upload every {{ formatInterval(probe.expectedUploadIntervalMs) }}</small>
          </span>
          <span role="cell">
            <span :class="overdueClass(probe.overdueMs)">{{ overdueLabel(probe.overdueMs) }}</span>
          </span>
          <span role="cell">
            <span :class="firmwareBadgeClass(probe)">{{ firmwareBadgeLabel(probe) }}</span>
          </span>
          <span role="cell" class="row-actions">
            <button
              class="icon-button"
              type="button"
              :aria-expanded="expandedProbeId === probe.id"
              title="Open probe details"
              :aria-label="`Open details for ${probe.displayName}`"
              @click.stop="void openProbeDetails(probe)"
            >
              <UserActionIcon name="details" />
            </button>
            <button
              v-if="canExecuteProbeOperations"
              class="icon-button"
              type="button"
              :disabled="!canApplyFirmwareUpdate(probe)"
              :title="updateActionLabel(probe)"
              :aria-label="`${updateActionLabel(probe)} for ${probe.displayName}`"
              @click.stop="applySelectedProbeFirmwareUpdate(probe)"
            >
              <UserActionIcon name="firmware-update" />
            </button>
            <button
              v-if="canManageProbeLifecycle"
              class="icon-button"
              type="button"
              :disabled="busyProbeId === probe.id"
              title="Rename probe"
              :aria-label="`Rename ${probe.displayName}`"
              @click.stop="renameSelectedProbe(probe)"
            >
              <UserActionIcon name="rename" />
            </button>
            <button
              v-if="canManageProbeLifecycle"
              class="icon-button icon-button--danger"
              type="button"
              :disabled="busyProbeId === probe.id"
              title="Delete probe"
              :aria-label="`Delete ${probe.displayName}`"
              @click.stop="deleteSelectedProbe(probe)"
            >
              <UserActionIcon name="delete" />
            </button>
          </span>
        </div>
        <Teleport to="body">
          <div
            v-if="expandedProbeId === probe.id"
            class="probe-details-drawer"
            role="dialog"
            :aria-label="`Details for ${probe.displayName}`"
          >
          <header class="probe-details-drawer__header">
            <div>
              <p class="eyebrow">Probe details</p>
              <h2>{{ probe.displayName }}</h2>
            </div>
            <button class="probe-details-drawer__close" type="button" aria-label="Close probe details" @click="closeProbeDetails">×</button>
            <nav class="probe-details-navigation" aria-label="Probe detail sections">
              <button :class="{ 'probe-details-navigation__item--active': activeProbeDetailsTab === 'settings' }" type="button" :aria-current="activeProbeDetailsTab === 'settings' ? 'page' : undefined" @click="activeProbeDetailsTab = 'settings'">Probe Settings</button>
              <button :class="{ 'probe-details-navigation__item--active': activeProbeDetailsTab === 'diagnostics' }" type="button" :aria-current="activeProbeDetailsTab === 'diagnostics' ? 'page' : undefined" @click="activeProbeDetailsTab = 'diagnostics'">Diagnostics</button>
              <button :class="{ 'probe-details-navigation__item--active': activeProbeDetailsTab === 'update-state' }" type="button" :aria-current="activeProbeDetailsTab === 'update-state' ? 'page' : undefined" @click="activeProbeDetailsTab = 'update-state'">Update State</button>
            </nav>
          </header>
          <section v-show="activeProbeDetailsTab === 'settings'" class="probe-details-drawer__section">
            <p class="eyebrow">Expected provider reconnect</p>
            <p class="settings-message">Qualifying outages remain measured and stored; this only changes their classification and reporting.</p>
            <div v-if="reconnectPolicyDrafts[probe.id]" class="settings-form reconnect-policy-form" :class="{ 'reconnect-policy-form--disabled': !reconnectPolicyDrafts[probe.id]!.enabled }">
              <label class="settings-checkbox settings-field--wide"><input v-model="reconnectPolicyDrafts[probe.id]!.enabled" type="checkbox"><span>Enable expected provider reconnect</span></label>
              <label><span>Timezone</span><input v-model="reconnectPolicyDrafts[probe.id]!.timezone" :disabled="!reconnectPolicyDrafts[probe.id]!.enabled" :list="`timezone-options-${probe.id}`" type="text" placeholder="Europe/Berlin" autocomplete="off"><datalist :id="`timezone-options-${probe.id}`"><option v-for="timezone in supportedTimeZones" :key="timezone" :value="timezone"></option></datalist></label>
              <label><span>Daily window start</span><TimePicker :model-value="reconnectPolicyDrafts[probe.id]!.windowStartLocal" :twelve-hour="usesTwelveHourClock" :ariaLabel="'Daily window start'" :disabled="!reconnectPolicyDrafts[probe.id]!.enabled" @update:model-value="reconnectPolicyDrafts[probe.id]!.windowStartLocal = $event" /></label>
              <label><span>Daily window end</span><TimePicker :model-value="reconnectPolicyDrafts[probe.id]!.windowEndLocal" :twelve-hour="usesTwelveHourClock" :ariaLabel="'Daily window end'" :disabled="!reconnectPolicyDrafts[probe.id]!.enabled" @update:model-value="reconnectPolicyDrafts[probe.id]!.windowEndLocal = $event" /></label>
              <label><span>Maximum interruption (minutes)</span><input :value="reconnectPolicyDrafts[probe.id]!.maxDurationMs / 60000" :disabled="!reconnectPolicyDrafts[probe.id]!.enabled" min="1" max="1440" type="number" inputmode="numeric" @input="reconnectPolicyDrafts[probe.id]!.maxDurationMs = Number(($event.target as HTMLInputElement).value) * 60000"></label>
              <button class="button button--primary button--compact" type="button" :disabled="busyProbeId === probe.id" @click="saveReconnectPolicy(probe)">Save reconnect policy</button>
            </div>
          </section>
          <section v-show="activeProbeDetailsTab === 'settings'" class="probe-details-drawer__section">
            <p class="eyebrow">Firmware policy</p>
            <div class="settings-form probe-policy-form">
              <dl>
                <div>
                  <dt>Tenant default</dt>
                  <dd>{{ probe.firmware.tenantDefaultChannel }} / {{ probe.firmware.tenantDefaultUpdateMode }}</dd>
                </div>
                <div>
                  <dt>Effective</dt>
                  <dd>{{ probe.firmware.effectiveChannel }} / {{ probe.firmware.effectiveUpdateMode }}</dd>
                </div>
              </dl>
              <template v-if="canExecuteProbeOperations">
                <label>
                  Channel override
                  <select
                    :value="policyDrafts[probe.id]?.channelOverride ?? probe.firmware.channelOverride"
                    @change="updatePolicyDraft(probe, 'channelOverride', ($event.target as HTMLSelectElement).value)"
                  >
                    <option value="inherit">Inherit tenant default</option>
                    <option value="stable">Stable</option>
                    <option value="beta">Beta</option>
                  </select>
                </label>
                <label>
                  Update mode override
                  <select
                    :value="policyDrafts[probe.id]?.updateModeOverride ?? probe.firmware.updateModeOverride"
                    @change="updatePolicyDraft(probe, 'updateModeOverride', ($event.target as HTMLSelectElement).value)"
                  >
                    <option value="inherit">Inherit tenant default</option>
                    <option value="manual">Manual</option>
                    <option value="automatic">Automatic</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </label>
                <button
                  class="button button--primary button--compact"
                  type="button"
                  :disabled="busyProbeId === probe.id"
                  @click="saveSelectedProbeFirmwarePolicy(probe)"
                >
                  Save policy
                </button>
              </template>
            </div>
          </section>
          <section v-show="activeProbeDetailsTab === 'update-state'" class="probe-details-drawer__section">
            <p class="eyebrow">Update state</p>
            <div class="probe-readonly-fields">
              <label><span>Current firmware</span><input readonly :value="probe.firmware.firmwareVersion ?? 'unknown'"></label>
              <label><span>Available firmware</span><input readonly :value="`${probe.firmware.targetVersion ?? 'none'}${probe.firmware.targetMandatory ? ' · mandatory' : ''}`"></label>
              <label><span>Manual request</span><input readonly :value="probe.firmware.manualUpdatePending ? 'pending' : 'none'"></label>
              <label><span>Last OTA report</span><input readonly :value="`${probe.firmware.lastUpdatePhase ?? 'none'}${probe.firmware.lastUpdateResult ? ` · ${probe.firmware.lastUpdateResult}` : ''}${probe.firmware.lastUpdateErrorCode ? ` · ${probe.firmware.lastUpdateErrorCode}` : ''}`"></label>
              <label><span>OTA reported at</span><input readonly :value="formatDateTime(probe.firmware.lastUpdateAt)"></label>
            </div>
          </section>
          <section v-show="activeProbeDetailsTab === 'diagnostics'" class="probe-details-drawer__section">
            <p class="eyebrow">Diagnostics</p>
            <p v-if="probe.uploadDiagnostics === null" class="muted-text">
              No upload diagnostics reported yet.
            </p>
            <div v-else class="probe-diagnostics">
              <section class="probe-diagnostics__group">
                <h3>Reported</h3>
                <label><span>Last reported</span><input readonly :value="formatDateTime(probe.uploadDiagnostics.lastReportedAt)"></label>
              </section>
              <section class="probe-diagnostics__group">
                <h3>Wi-Fi</h3>
                <label><span>Connection</span><input readonly :value="formatConnectionState(probe.uploadDiagnostics.wifi?.stationConnected)"></label>
                <label>
                  <span>RSSI</span>
                  <input readonly :value="formatRssi(probe.uploadDiagnostics.wifi?.rssi)">
                  <span class="quality-meter" :aria-label="`Wi-Fi signal strength: ${formatRssi(probe.uploadDiagnostics.wifi?.rssi)}`"><span :class="rssiQualityClass(probe.uploadDiagnostics.wifi?.rssi)" :style="{ width: rssiQualityPercent(probe.uploadDiagnostics.wifi?.rssi) }"></span></span>
                </label>
                <label><span>Channel</span><input readonly :value="formatNumber(probe.uploadDiagnostics.wifi?.channel)"></label>
              </section>
              <section class="probe-diagnostics__group">
                <h3>Upload</h3>
                <label><span>Consecutive failures</span><input readonly :value="formatNumber(probe.uploadDiagnostics.upload?.consecutiveFailures)"></label>
                <label><span>Failures in 24 hours</span><input readonly :value="formatNumber(probe.uploadDiagnostics.upload?.failuresLast24h)"></label>
                <label><span>Last successful upload</span><input readonly :value="formatDateTime(probe.uploadDiagnostics.upload?.lastSuccessfulUploadAt ?? probe.uploadDiagnostics.lastReportedAt)"></label>
                <label><span>Time since successful upload</span><input readonly :value="formatTimeSince(probe.uploadDiagnostics.upload?.lastSuccessfulUploadAt ?? probe.uploadDiagnostics.lastReportedAt)"></label>
              </section>
              <section class="probe-diagnostics__group">
                <h3>Queue</h3>
                <label><span>Usage</span><input readonly :value="formatQueueUsage(probe.uploadDiagnostics)"></label>
                <label><span>Measurements</span><input readonly :value="formatNumber(probe.uploadDiagnostics.storage?.measurementRecords)"></label>
                <label><span>Wrapped</span><input readonly :value="probe.uploadDiagnostics.storage?.wrapped === true ? 'yes' : 'no'"></label>
              </section>
              <section class="probe-diagnostics__group">
                <h3>System</h3>
                <label><span>Chip temperature</span><input readonly :value="formatTemperature(probe.uploadDiagnostics.chipTemperatureCelsius)"></label>
                <label>
                  <span>Minimum free heap</span>
                  <input readonly :value="formatBytes(probe.uploadDiagnostics.heap?.minimumFreeBytes)">
                  <span class="heap-meter" :aria-label="`Minimum free heap: ${formatBytes(probe.uploadDiagnostics.heap?.minimumFreeBytes)}`"><span :class="heapHealthClass(probe.uploadDiagnostics.heap?.minimumFreeBytes)" :style="{ width: heapHealthPercent(probe.uploadDiagnostics.heap?.minimumFreeBytes) }"></span></span>
                </label>
                <label>
                  <span>Largest free heap block</span>
                  <input readonly :value="formatBytes(probe.uploadDiagnostics.heap?.largestFreeBlockBytes)">
                  <span class="heap-meter" :aria-label="`Largest free heap block: ${formatBytes(probe.uploadDiagnostics.heap?.largestFreeBlockBytes)}`"><span :class="heapHealthClass(probe.uploadDiagnostics.heap?.largestFreeBlockBytes)" :style="{ width: heapHealthPercent(probe.uploadDiagnostics.heap?.largestFreeBlockBytes) }"></span></span>
                </label>
                <label>
                  <span>Free heap</span>
                  <input readonly :value="formatBytes(probe.uploadDiagnostics.heap?.freeBytes)">
                  <span class="heap-meter" :aria-label="`Free heap: ${formatBytes(probe.uploadDiagnostics.heap?.freeBytes)}`"><span :class="heapHealthClass(probe.uploadDiagnostics.heap?.freeBytes)" :style="{ width: heapHealthPercent(probe.uploadDiagnostics.heap?.freeBytes) }"></span></span>
                </label>
              </section>
              <section class="probe-diagnostics__group">
                <h3>Boot</h3>
                <label><span>Boot ID</span><input readonly :value="probe.currentBootId ?? 'unknown'"></label>
                <label><span>Uptime</span><input readonly :value="formatNullableDuration(probe.uploadDiagnostics.uptimeMs)"></label>
                <label><span>Boot count</span><input readonly :value="formatNumber(probe.uploadDiagnostics.bootCount)"></label>
                <label><span>Reset reason</span><input readonly :value="formatResetReason(probe.uploadDiagnostics.resetReason, probe.uploadDiagnostics.resetReasonLabel)"></label>
              </section>
              <section class="probe-diagnostics__group">
                <h3>Probe</h3>
                <label><span>Probe ID</span><input readonly :value="probe.id"></label>
                <label><span>Probe model</span><input readonly :value="probe.probeModel ?? 'unknown'"></label>
                <label><span>OTA capable</span><input readonly :value="probe.firmware.otaCapable ? 'yes' : 'no'"></label>
                <label><span>Configuration applied</span><input readonly :value="`v${probe.reportedAppliedConfigurationVersion ?? '—'}`"></label>
                <label><span>Configuration desired</span><input readonly :value="`v${probe.desiredConfigurationVersion ?? '—'}`"></label>
              </section>
            </div>
          </section>
          </div>
        </Teleport>
        </template>
      </div>
    </section>

    <div v-if="enrollmentModalOpen" class="app-dialog-backdrop probe-enrollment-modal-backdrop">
      <section class="app-dialog probe-enrollment-modal" role="dialog" aria-modal="true" aria-labelledby="probe-enrollment-title">
        <header class="app-dialog__header">
          <p class="eyebrow">Probe enrollment</p>
          <h2 id="probe-enrollment-title">Add Probe</h2>
        </header>

        <label v-if="enrollmentTicket" class="app-dialog__input">
          <span>Name · optional</span>
          <input
            v-model="enrollmentRequestedDisplayName"
            type="text"
            maxlength="120"
            autocomplete="off"
          />
          <small v-if="enrollmentDisplayNameError" class="form-error">{{ enrollmentDisplayNameError }}</small>
        </label>
        <p v-if="creatingEnrollment" class="app-dialog__message">Creating deployment code…</p>
        <p v-else-if="enrollmentError" class="form-error">{{ enrollmentError }}</p>
        <template v-else-if="enrollmentTicket">
          <p class="app-dialog__message">Enter this code on the probe setup UI. It can be used once.</p>
          <div class="probe-enrollment-modal__cards">
            <div class="deployment-code-card deployment-code-card--modal">
              <p class="eyebrow">Deployment code</p>
              <div class="deployment-code">{{ enrollmentTicket.displayCode }}</div>
              <p>Expires in {{ timerText }}.</p>
            </div>
            <div class="enrollment-payload-panel">
              <div>
                <p class="eyebrow">LacunaProbe Software payload</p>
                <strong>enrollment.json or setup code</strong>
              </div>
              <code>{{ enrollmentSetupCodePreview }}</code>
              <div class="enrollment-payload-panel__actions">
                <button class="button button--ghost button--compact" type="button" @click="downloadEnrollmentFile">
                  Download JSON
                </button>
                <button class="button button--ghost button--compact" type="button" @click="downloadEnrollmentSetupCode">
                  Download code
                </button>
                <button class="button button--quiet button--compact" type="button" @click="copyEnrollmentSetupCode">
                  Copy code
                </button>
              </div>
              <p v-if="enrollmentPayloadStatusMessage" class="muted-text">{{ enrollmentPayloadStatusMessage }}</p>
            </div>
          </div>
          <p v-if="enrollmentStatusMessage" class="notice">{{ enrollmentStatusMessage }}</p>
          <p v-else class="notice">Waiting for setup.</p>
        </template>

        <footer class="app-dialog__actions">
          <button class="button button--danger" type="button" :disabled="cancellingEnrollment" @click="cancelEnrollment">
            {{ cancellingEnrollment ? "Cancelling…" : "Cancel" }}
          </button>
        </footer>
      </section>
    </div>
  </AppShell>
</template>
